import { IconArrowDown, IconArrowUp, IconDotsVertical, IconPin, IconShieldFilled } from "@tabler/icons-react"
import { type FC, useState } from "react"
import styled from "styled-components"

import { type CommunityPostReactionType, type PersonalizedCommunityPost } from "@forento/core/models/community"
import { toDateTimeString } from "@forento/core/utilities/date"
import { toCountFormat } from "@forento/core/utilities/number"
import { exhaustiveGuard } from "@forento/core/utilities/switch"
import CommunityPostReactionPicker from "@forento/ui/components/CommunityPostReactionPicker"
import CommunityPostReactionSummary from "@forento/ui/components/CommunityPostReactionSummary"
import CommunityProfileAvatar from "@forento/ui/components/CommunityProfileAvatar"
import LexicalViewer from "@forento/ui/components/LexicalViewer"
import { VimeoPlayer, YoutubePlayer } from "@forento/ui/components/VideoPlayer"

import Button from "~/components/Button"
import { CommentIcon, DeleteIcon, EditIcon, LikeIcon } from "~/components/Icon"
import PostReactions from "~/components/PostReactions"
import { useAlert } from "~/contexts/AlertContext"
import { useToast } from "~/contexts/ToastContext"
import { usePlatform, useUser } from "~/contexts/UserContext"
import useDropdownMenu, { type DropdownItem } from "~/hooks/useDropdownMenu"
import { toRelativeDateString } from "~/utilities/date"
import { fonts, lightTextColor } from "~/utilities/styles"
import trpc, { query } from "~/utilities/trpc"

import Attachment from "./Attachment"
import EditModal from "./EditModal"
import PollOption from "./PollOption"
import useImageCarousel from "./useImageCarousel"

type Props = {
	post: PersonalizedCommunityPost
	isSelected: boolean
	onSelect(): void
	onChange(): Promise<unknown>
	onCategorySelect?(categoryId: number): void
	showChannel?: boolean
	allowPinning?: boolean
}
const Post: FC<Props> = ({ post, isSelected, onSelect, onChange, onCategorySelect, showChannel, allowPinning }) => {
	const platform = usePlatform()!
	const user = useUser().user!
	const alert = useAlert()
	const toast = useToast()

	const [isReactionsModalOpen, setReactionsModalOpen] = useState(false)
	const [isEditModalOpen, setEditModalOpen] = useState(false)

	const pin = query.community.togglePostPin.useMutation({
		async onSuccess() {
			const isPinned = post.pinIndex !== null
			await onChange()
			toast.showSuccess(isPinned ? "Post unpinned" : "Post pinned")
		},
	})
	const movePinUp = query.community.movePinUp.useMutation({
		async onSuccess() {
			await onChange()
			toast.showSuccess("Post moved up")
		},
	})
	const movePinDown = query.community.movePinDown.useMutation({
		async onSuccess() {
			await onChange()
			toast.showSuccess("Post moved down")
		},
	})

	const dropdownItems: DropdownItem[] = []
	if (allowPinning) {
		const isPinned = post.pinIndex !== null
		dropdownItems.push({ label: isPinned ? "Unpin" : "Pin", icon: IconPin, onClick: () => pin.mutate(post.id) })

		if (isPinned) {
			dropdownItems.push({ label: "Move up", icon: IconArrowUp, onClick: () => movePinUp.mutate(post.id) })
			dropdownItems.push({ label: "Move down", icon: IconArrowDown, onClick: () => movePinDown.mutate(post.id) })
		}
	}
	if (user.communityProfileId === post.profile.id) {
		dropdownItems.push({ label: "Edit", icon: EditIcon, onClick: () => setEditModalOpen(true) })
	}
	dropdownItems.push({ label: "Delete", icon: DeleteIcon, isDanger: true, onClick: handleDelete })

	const dropdownMenu = useDropdownMenu(dropdownItems)

	async function handleVote(pollOptionId: number | null) {
		await trpc.community.setPostVote.mutate({
			postId: post.id,
			pollOptionId:
				pollOptionId !== null && !post.pollOptions.find(x => x.id === pollOptionId)?.hasUserSelected
					? pollOptionId
					: null,
		})
		await onChange()
	}

	async function handleReact(type: CommunityPostReactionType | null) {
		if (type !== null) {
			await trpc.community.setPostReaction.mutate({ postId: post.id, type })
		} else {
			await trpc.community.deletePostReaction.mutate(post.id)
		}
		await onChange()
	}

	async function handleDelete() {
		const dialog = await alert.confirm(
			"Delete post",
			"Are you sure you want to delete this post? This action cannot be undone.",
		)
		if (!dialog.result) return
		await trpc.community.deletePost.mutate(post.id)
		dialog.close()
		await onChange()
	}

	const channelName = platform.content.multipleChannels && showChannel ? post.channel.name : null
	const category = post.category

	return (
		<Container $isSelected={isSelected}>
			{dropdownMenu.component}
			<PostReactions
				postId={isReactionsModalOpen ? post.id : null}
				onClose={() => setReactionsModalOpen(false)}
			/>
			<EditModal
				postId={post.id}
				isOpen={isEditModalOpen}
				onClose={() => setEditModalOpen(false)}
				onReloadPosts={onChange}
			/>
			<Header>
				<div>
					{(channelName !== null || category !== null) && (
						<Category>
							{channelName}
							{channelName !== null && category !== null && " > "}
							{category !== null &&
								(onCategorySelect ? (
									<button
										type="button"
										title={`Show posts in ${category.label}`}
										onClick={() => onCategorySelect(category.id)}
										className="hover:text-primary cursor-pointer hover:underline"
									>
										{category.label}
									</button>
								) : (
									category.label
								))}
						</Category>
					)}
					<Button onClick={onSelect}>
						{post.pinIndex !== null && <IconPin className="mr-2 h-6 w-6" />}
						<Title>{post.title}</Title>
					</Button>
				</div>
				<button
					type="button"
					aria-label="Post options"
					className="focus-visible:outline-primary cursor-pointer rounded-md focus-visible:outline-2 focus-visible:outline-offset-2"
					{...dropdownMenu.button}
				>
					<IconDotsVertical className="size-6" />
				</button>
			</Header>
			<LexicalViewer html={post.htmlContent} className="mx-6 mb-3" />
			{post.images.length > 0 && <PostImages images={post.images} />}
			{post.video !== null &&
				(post.video.type === "youtube" ? (
					<YoutubePlayer videoId={post.video.id} className="mx-6 mb-3 max-w-xl rounded-lg" />
				) : post.video.type === "vimeo" ? (
					<VimeoPlayer videoId={post.video.id} className="mx-6 mb-3 max-w-xl rounded-lg" />
				) : (
					exhaustiveGuard(post.video.type)
				))}
			{post.pollOptions.length > 0 && (
				<PollOptions>
					{post.pollOptions.map((option, index) => (
						<PollOption
							key={index}
							option={option}
							onChange={isSelected => (isSelected ? handleVote(option.id) : handleVote(null))}
						/>
					))}
				</PollOptions>
			)}
			{post.attachments.length > 0 && (
				<Attachments>
					{post.attachments.map(attachment => (
						<Attachment key={attachment.id} attachment={attachment} />
					))}
				</Attachments>
			)}
			<Author>
				<CommunityProfileAvatar profile={post.profile} size={38} />
				{post.profile.firstName} {post.profile.lastName}
				{post.profile.isCreator && <IconShieldFilled className="size-4 text-indigo-500" title="Admin" />}
				<Date title={toDateTimeString(post.createDate)}>
					{toRelativeDateString(post.createDate, "narrow")}
					{post.editDate && (
						<>
							<DotIcon />
							Edited {toRelativeDateString(post.editDate, "narrow")}
						</>
					)}
				</Date>
			</Author>
			<Footer>
				<FooterItem>
					<CommunityPostReactionPicker
						value={post.userReactionType}
						onSelect={handleReact}
						className={post.userReactionType !== null ? "text-primary" : undefined}
					>
						<FooterIcon as={LikeIcon} />
					</CommunityPostReactionPicker>
					<Button onClick={() => setReactionsModalOpen(true)}>
						<CommunityPostReactionSummary counts={post.reactionCounts} />
					</Button>
				</FooterItem>
				<FooterItem as={Button} onClick={onSelect}>
					<FooterIcon as={CommentIcon} />
					{toCountFormat(post.commentsCount)}
				</FooterItem>
			</Footer>
		</Container>
	)
}

const PostImages: FC<{ images: { id: number; filePath: string }[] }> = ({ images }) => {
	const carousel = useImageCarousel(images)
	const visibleImages = images.slice(0, 4)
	const hiddenImageCount = images.length - visibleImages.length
	const overflowImageIndex = visibleImages.length - 1

	return (
		<div className="mx-6 mb-3 grid max-w-2xl grid-cols-2 gap-2">
			{carousel.element}
			{visibleImages.map((image, index) => (
				<Button
					onClick={() => carousel.showIndex(index)}
					key={image.id}
					className="relative w-full"
					aria-label={
						index === overflowImageIndex && hiddenImageCount > 0
							? `View all ${images.length} post images`
							: undefined
					}
				>
					<img
						src={image.filePath}
						alt={`Post image ${index + 1}`}
						className="aspect-square w-full rounded-lg object-cover"
					/>
					{index === overflowImageIndex && hiddenImageCount > 0 && (
						<span
							aria-hidden="true"
							className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-black/50 text-xl font-semibold text-white"
						>
							+{hiddenImageCount}
						</span>
					)}
				</Button>
			))}
		</div>
	)
}

const Container = styled.div<{ $isSelected: boolean }>`
	width: 100%;
	border: 1px solid ${props => (props.$isSelected ? props.theme.primaryColor : "var(--color-component-border)")};
	border-radius: 24px;
	display: flex;
	flex-direction: column;
`

const Header = styled.div`
	display: flex;
	gap: 24px;
	justify-content: space-between;
	margin: 24px 24px 12px;
`

const Category = styled.p`
	font-size: 14px;
	color: ${lightTextColor};
`

const Title = styled.h2`
	font-weight: 700;
	font-size: 20px;
`

const PollOptions = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin: 0 24px 12px;
`

const Attachments = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin: 0 24px 12px;
`

const Author = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin: 0 24px 20px;
`

const Date = styled.div`
	color: #949494;
	display: flex;
	align-items: center;
	gap: 8px;
`

const DotIcon = styled.div`
	width: 4px;
	height: 4px;
	background-color: #949494;
	border-radius: 50%;
`

const Footer = styled.div`
	display: flex;
	align-items: center;
	gap: 24px;
	padding: 16px 24px;
	border-top: 1px solid #dedede;

	> * + * {
		border-left: 1px solid #dedede;
		padding-left: 24px;
	}
`

const FooterItem = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	font-family: ${fonts.mono};
`

const FooterIcon = styled.div`
	width: 20px;
	height: 20px;
`

export default Post
